// =============================================================================
// features/crm/components/LeadStatCards.tsx — Cartões do topo do CRM (F58-S08,
// CRM-05). Os números vêm de `GET /api/leads/stats` com os MESMOS filtros da
// lista — nunca da página de 20 que a tabela está mostrando.
//
// Estados: loading = skeleton no formato final; erro = aviso com retry (sem
// número chutado); sucesso = 4 Stat canônicos (DS §9.8).
// =============================================================================

import type { LeadStatsResponse } from '@elemento/shared-schemas';
import * as React from 'react';

import { Stat } from '../../../components/ui/Stat';
import { buildStatCards } from '../listFilters';

function StatsSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-surface-1 p-5 animate-pulse"
          style={{ boxShadow: 'var(--elev-2)', height: 96 }}
          aria-hidden="true"
        >
          <div
            className="h-3 w-20 rounded-xs mb-3"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div className="h-8 w-16 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
        </div>
      ))}
    </div>
  );
}

interface LeadStatCardsProps {
  stats: LeadStatsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Há filtro ativo? Muda só a descrição do total. */
  filtered: boolean;
}

export function LeadStatCards({
  stats,
  isLoading,
  isError,
  filtered,
}: LeadStatCardsProps): React.JSX.Element | null {
  if (stats) {
    return (
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.05s both' }}
      >
        {buildStatCards(stats, filtered).map((card) => (
          <Stat
            key={card.key}
            label={card.label}
            value={card.value}
            description={card.description}
          />
        ))}
      </div>
    );
  }
  if (isLoading) return <StatsSkeleton />;
  if (isError) {
    return (
      <p
        role="status"
        className="rounded-md border border-border-subtle px-4 py-3 font-sans text-sm text-ink-3"
        style={{ background: 'var(--bg-elev-2)' }}
      >
        Não foi possível carregar os totais. A lista abaixo continua valendo.
      </p>
    );
  }
  return null;
}
